import { DefaultDict } from "./defaultdict";
import { CardsGroupStatus,
        cardsGroupInRevisionStatus,
        cardsGroupsWithHistoricalObservationsColors,
        colors,
        paidPhysicalProgressColors,
        datesColors, 
        weeksColors,
        monthsColors} from "../constants/constants";
import { dateIsOlderThanToday,
        daysDifferenceBetweenDates,
        getActualDatetime,
        getDaysDiffBetweenDates,
        mixRgbColors,
        pointsAreEqual,
        removeDuplicates } from "./utils";
import { getMonthNumberFromDates, getWeekNumberFromDates, getTotalWMonthsFromDates, getTotalWeekFromDates } from "./getter";

export function removeRepeatedFeatures(features) {
    let featureIds = []
    let filteredFeatures = []
    for (const feature of features) {
        if (!featureIds.includes(feature.id)){
            featureIds.push(feature.id)
            filteredFeatures.push(feature)
        }
    }
    return filteredFeatures
}

export function getSeparableCardsGroups(cardsGroupData, props, separableCardsGroups) {
    if (separableCardsGroups.includes(cardsGroupData.id)) return;
    let figuregroupId = cardsGroupData.attributes.figuregroup_id;
    let figures = props.projectData.figuregroups_map[figuregroupId];
    if (figures 
        && figures.length > 1 
        && cardsGroupData.attributes.status === CardsGroupStatus.STAND_BY) {
        separableCardsGroups.push(cardsGroupData.id);
    }
}

export function getMergeableCardsGroups(props, cardsGroupIds, newCardsGroupsData) {
    let cardsGroupsGrouped = new DefaultDict(Array);
    let referenceCardsGroup = null;
    for (const cardsGroupId of cardsGroupIds) {
        let cardsGroupData = props.projectData.cards_groups[cardsGroupId];
        if (!cardsGroupData) {
            if (newCardsGroupsData) {
                cardsGroupData = newCardsGroupsData[cardsGroupId];
                if (!cardsGroupData) {
                    continue
                }
            }
        }

        if (cardsGroupData.attributes.status !== CardsGroupStatus.STAND_BY) continue;
        let customcardData = props.projectData.customcards[cardsGroupData.attributes.customcard_id];
        const hasAppartment = customcardData.attributes.appartment_id && customcardData.attributes.appartment_id.length;
        if (hasAppartment) continue;
        if (!referenceCardsGroup) referenceCardsGroup = cardsGroupData;
        if (cardsGroupData.attributes.level_id !== referenceCardsGroup.attributes.level_id) continue;
        if (cardsGroupData.attributes.card_category_id !== referenceCardsGroup.attributes.card_category_id) continue;
        let protocolId = cardsGroupData.attributes.protocol_id;
        if (cardsGroupsGrouped[protocolId]) {
            cardsGroupsGrouped[protocolId].push(cardsGroupData);
        }
    }
    let mergeableCardsGroupIds = [];
    let figureIds = [];
    for (const cardsGroupsGroup of Object.values(cardsGroupsGrouped)) {
        if (cardsGroupsGroup.length < 2) continue;
        let figuregroupIds = [];
        for (const cardsGroupData of cardsGroupsGroup) {
            if (!figuregroupIds.includes(cardsGroupData.attributes.figuregroup_id)) {
                figuregroupIds.push(cardsGroupData.attributes.figuregroup_id);
                mergeableCardsGroupIds.push(cardsGroupData.id);
                let cardsGroupFigureIds = props.projectData.figuregroups_map[cardsGroupData.attributes.figuregroup_id];
                figureIds = [...figureIds, ...cardsGroupFigureIds];
            }
        }
        
    }
    return {
        cardsGroupIds: removeDuplicates(mergeableCardsGroupIds),
        figureIds: removeDuplicates(figureIds)
    }
}

export function getHoveredFeatures(features) {
    const hoveredFeatures = [];
    for (let i = 0; i < features.length; i++) {
        if (features[i].state.hover) {
            hoveredFeatures.push(features[i]);
        }
    }
    return hoveredFeatures;
}

export function calculateStatusColor(props, filters, feature) {
    let featureColors = [];
    for (const cardsGroupId of feature.properties.cards_group_ids) {
        let cardsGroupData = props.projectData.cards_groups[cardsGroupId];
        if (cardsGroupFiltersPass(filters, cardsGroupData, feature)) {
            featureColors.push(colors[cardsGroupData.attributes.status]);
        }
    }
    return mixRgbColors(featureColors);
}

export function calculateAverageProgress(props, filters, feature) {
    let allProgress = [];
    for (const cardsGroupId of feature.properties.cards_group_ids) {
        let cardsGroupData = props.projectData.cards_groups[cardsGroupId];
        if (cardsGroupFiltersPass(filters, cardsGroupData, feature)) {
            let progress = cardsGroupData.attributes.physical_progress;
            progress = progress ? progress : 0;
            if (progress >= 99.9 && cardsGroupData.attributes.status === CardsGroupStatus.CLOSED) {
                progress = 150;
            }
            allProgress.push(progress);
        }
    }
    return allProgress.reduce((prev, next) => prev + next, 0) / allProgress.length;
}

export function calculateAverageDelay(props, filters, feature) {
    let delayedCount = 0;
    let totalCount = 0;
    const actualDate = getActualDatetime();
    for (const cardsGroupId of feature.properties.cards_group_ids) {
        const cardsGroupData = props.projectData.cards_groups[cardsGroupId];
        if (cardsGroupFiltersPass(filters, cardsGroupData, feature)) {
            const status = cardsGroupData.attributes.status;
            const finishDate = cardsGroupData.attributes.finish_date;
            const cardsGroupIsInRevision = cardsGroupInRevisionStatus.includes(status);
            if (!cardsGroupIsInRevision) continue;
            totalCount ++;
            if (!finishDate) continue;
            const isDelayed = dateIsOlderThanToday(finishDate);
            if (isDelayed) {
                const delayedDays = getDaysDiffBetweenDates(finishDate, actualDate);
                if (delayedDays === 1) delayedCount += 1;
                else if (delayedDays > 0 && delayedDays <= 3) delayedCount += 3;
                else if (delayedDays > 3 && delayedDays <= 5) delayedCount += 5;
                else if (delayedDays > 5) delayedCount += 7;
            }
        }
    }
    if (!totalCount) return 0;
    return delayedCount / totalCount;
}

export function calculatePaidPhysicalProgress(props, filters, feature) {
    let finalColor = 'white';

    for (const cardsGroupId of feature.properties.cards_group_ids) {
        const cardsGroupData = props.projectData.cards_groups[cardsGroupId];
        if (cardsGroupFiltersPass(filters, cardsGroupData, feature)) {
            const paidPercentage = cardsGroupData.attributes.paid_percentage || 0;
            const cardGroupStatus = cardsGroupData.attributes.status

            if (paidPercentage < 100 && cardGroupStatus !== 'closed') {
                finalColor = paidPhysicalProgressColors.notComplete;
                break;
            } else if (paidPercentage < 100 && cardGroupStatus === 'closed') {
                finalColor = paidPhysicalProgressColors.physicalComplete;
                break;
            } else if (paidPercentage === 100 && cardGroupStatus !== 'closed') {
                finalColor = paidPhysicalProgressColors.paidComplete;
                break;
            } else if (paidPercentage === 100 && cardGroupStatus === 'closed') {
                finalColor = paidPhysicalProgressColors.fullyComplete;
                break;
            }
        }
    }

    return finalColor;
}

export function calculateHistoricalObservations(props, filters, feature) {
    let finalColor = 'white';
    for (const cardsGroupId of feature.properties.cards_group_ids) {
        const cardsGroupData = props.projectData.cards_groups[cardsGroupId];
        if (cardsGroupFiltersPass(filters, cardsGroupData, feature)) {
            if (props.cardsGroupsWithHistoricalObservations.includes(cardsGroupId)){
                finalColor = cardsGroupsWithHistoricalObservationsColors.withHistoricalObservations;
                break;
            }
        }
    }

    return finalColor;
}

export function calculateDates(props, filters, feature) {
    let finalColor = 'white';
    const daysDifference = daysDifferenceBetweenDates(filters.day.first, filters.day.last);
    if (daysDifference <= 6) {
        const weekDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        for (const cardsGroupId of feature.properties.cards_group_ids) {
            const cardsGroupData = props.projectData.cards_groups[cardsGroupId];
            if (cardsGroupFiltersPass(filters, cardsGroupData, feature)) {
                if (cardsGroupData.attributes.start_date) {
                    const date = new Date(cardsGroupData.attributes.start_date);
                    const weekDay = weekDays[date.getDay()];
                    finalColor = datesColors[weekDay];
                    break;
                }
            }
        }
    }
    if (daysDifference > 6 && daysDifference <= 56) {
        const weeks = ['Semana 1', 'Semana 2', 'Semana 3', 'Semana 4', 'Semana 5', 'Semana 6', 'Semana 7', 'Semana 8'];
        for (const cardsGroupId of feature.properties.cards_group_ids) {
            const cardsGroupData = props.projectData.cards_groups[cardsGroupId];
            if (cardsGroupFiltersPass(filters, cardsGroupData, feature)) {
                if (cardsGroupData.attributes.start_date) {
                    const weekNumber = getWeekNumberFromDates(filters.day.first, filters.day.last, cardsGroupData.attributes.start_date);
                    if (weekNumber === -1) continue;
                    const weekName = weeks[weekNumber - 1];
                    finalColor = weeksColors[weekName];
                    break;
                }
            }
        }
    }
    if (daysDifference > 56) {
        const months = ['Mes 1', 'Mes 2', 'Mes 3', 'Mes 4', 'Mes 5', 'Mes 6', 'Mes 7', 'Mes 8 +'];
        for (const cardsGroupId of feature.properties.cards_group_ids) {
            const cardsGroupData = props.projectData.cards_groups[cardsGroupId];
            if (cardsGroupFiltersPass(filters, cardsGroupData, feature)) {
                if (cardsGroupData.attributes.start_date) {
                    const monthNumber = getMonthNumberFromDates(filters.day.first, filters.day.last, cardsGroupData.attributes.start_date);
                    if (monthNumber === -1) continue;
                    let monthName = ''
                    if (monthNumber >= 8) {
                        monthName = 'Mes 8 +'
                    } else {
                        monthName = months[monthNumber - 1];
                    }
                    finalColor = monthsColors[monthName];
                    break;
                }
            }
        }
    }

    return finalColor;
}

export function handleFeaturesVisibility(map, props) {
    const featureIds = [];
    const cardsGroupIds = [];
    for (const [levelId, featuresGroup] of Object.entries(props.featuresGroups)) {
        if (props.filters.levelIds.length > 0 && !props.filters.levelIds.includes(parseInt(levelId))) continue;
        let stageId = props.projectData.levels[levelId].attributes.stage_id;
        if (props.filters.stageIds.length > 0 && !props.filters.stageIds.includes(stageId)) continue;
        addVisibleFeatures(
            map.filters,
            featuresGroup,
            featureIds,
            cardsGroupIds,
            props.projectData.cards_groups
        );
    }
    return [featureIds, cardsGroupIds];
}

function addVisibleFeatures(filters, featuresGroup, featureIds, cardsGroupIds, cardsGroupsData) {
    let featureAdded = false;
    for (const feature of featuresGroup) {
        featureAdded = false;
        for (const cardsGroupId of feature.properties.cards_group_ids) {
            if (cardsGroupFiltersPass(filters, cardsGroupsData[cardsGroupId], feature)) {
                if(!cardsGroupIds.includes(cardsGroupId)) {
                    cardsGroupIds.push(cardsGroupId);
                }
                if (!featureAdded) {
                    featureIds.push(feature.id);
                    featureAdded = true;
                }
            }
        }
    }
}

export function groupCardsGroupIds(props, cardsGroupIds, newCardsGroupsData={}) {
    const groupedCardsGroupIds = {
        [CardsGroupStatus.STAND_BY]: [],
        [CardsGroupStatus.OPEN]: [],
        [CardsGroupStatus.IN_PROGRESS]: [],
        [CardsGroupStatus.WITH_NO_CONFORMITIES]: [],
        [CardsGroupStatus.CLOSED]: [],
        [CardsGroupStatus.UNFULFILLED]: [],
        byCategory: {},
        separable: [],
        mergeable: getMergeableCardsGroups(props, cardsGroupIds, newCardsGroupsData),
        equalProtocols: true,
        equalCategories: true,
        closedSelectedOnly: true,
        hasProtocols: false
    };
    let firstProtocolId = null;
    let firstCardCategoryId = null;
    for (const cardsGroupId of cardsGroupIds) {
        let cardsGroupData = props.projectData.cards_groups[cardsGroupId.toString()];
        if (!cardsGroupData) {
            if (newCardsGroupsData) {
                cardsGroupData = newCardsGroupsData[cardsGroupId];
                if (!cardsGroupData) {
                    continue
                }
            }
        }

        groupByStatus(cardsGroupData, groupedCardsGroupIds);
        groupByCategory(cardsGroupData, groupedCardsGroupIds);
        getSeparableCardsGroups(cardsGroupData, props, groupedCardsGroupIds.separable);
        if (!firstProtocolId) firstProtocolId = cardsGroupData.attributes.protocol_id;
        if (!firstCardCategoryId) firstCardCategoryId = cardsGroupData.attributes.card_category_id;
        if (cardsGroupData.attributes.protocol_id) {
            groupedCardsGroupIds.hasProtocols = true;
        }
        if (firstProtocolId !== cardsGroupData.attributes.protocol_id) {
            groupedCardsGroupIds.equalProtocols = false;
        }
        if (firstCardCategoryId !== cardsGroupData.attributes.card_category_id) {
            groupedCardsGroupIds.equalCategories = false;
        }
        if (cardsGroupData.attributes.status !== CardsGroupStatus.CLOSED) {
            groupedCardsGroupIds.closedSelectedOnly = false;
        }
    }
    return groupedCardsGroupIds
}

function groupByStatus(cardsGroupData, groupedCardsGroupIds) {
    let cardsGroupId = cardsGroupData.id;
    let status = cardsGroupData.attributes.status;
    groupedCardsGroupIds[status].push(cardsGroupId);
}

function groupByCategory(cardsGroupData, groupedCardsGroupIds) {
    let categoryId = cardsGroupData.attributes.card_category_id;
    let categoryIdStr = categoryId ? categoryId.toString() : 'no_category';
    if (!Object.keys(groupedCardsGroupIds.byCategory).includes(categoryIdStr)){
        groupedCardsGroupIds.byCategory[categoryIdStr] = [cardsGroupData]
    } else {
        groupedCardsGroupIds.byCategory[categoryIdStr].push(cardsGroupData)
    }
}

export function cardsGroupFiltersPass(filters, cardsGroupData, feature=null) {
    if (!cardsGroupData) return false;
    if (filters.timeline) {
        if (filters.day.first > filters.day.last) {
            return false
        }
        switch (filters.timelineSelect) {
            case 'start_date':
                if (!cardsGroupData.attributes.start_date) return false;
                let startDate = cardsGroupData.attributes.start_date.slice(0,10)
                if (startDate < filters.day.first || 
                    startDate > filters.day.last) {
                    return false;
                }
                break;
            case 'last_update_datetime':
                if (!cardsGroupData.attributes.last_update_datetime) return false;
                let lastUpdateDatetime = cardsGroupData.attributes.last_update_datetime.slice(0,10)
                if (lastUpdateDatetime < filters.day.first || 
                    lastUpdateDatetime > filters.day.last) {
                    return false;
                }
                break;
            case 'finish_date':
                if (!cardsGroupData.attributes.finish_date) return false;
                let finishDate = cardsGroupData.attributes.finish_date.slice(0,10)
                if (finishDate < filters.day.first || 
                    finishDate > filters.day.last) {
                    return false;
                }
                break;
            default:
                break;
        }
        
    }
    if (filters.cardsGroupIds) {
        if (filters.cardsGroupIds.indexOf(cardsGroupData.id) === -1) return false;
    }
    if (filters.levelIds.length) {
        if (filters.levelIds.indexOf(cardsGroupData.attributes.level_id) === -1) return false;
    }
    if (filters.protocolIds.length) {
        if (filters.protocolIds.indexOf(cardsGroupData.attributes.protocol_id) === -1) return false;
    }
    if (filters.status.length) {
        if (filters.status.indexOf(cardsGroupData.attributes.status) === -1) return false;
    }
    if (filters.appartmentIds.length) {
        if (filters.appartmentIds.indexOf(cardsGroupData.attributes.appartment_id) === -1) return false;
    }
    if (filters.cardCategoryIds.length) {
        if (filters.cardCategoryIds.indexOf(cardsGroupData.attributes.card_category_id) === -1) return false;
    }
    if (filters.zoneIds.length) {
        if (filters.zoneIds.indexOf(cardsGroupData.attributes.zone_id) === -1) return false;
    }
    if (filters.customcardTypes.length === 0) {
        return false
    }
    if (filters.customcardId === null) {
        return false;
    }
    if (filters.customcardId.length) {
        if (!filters.customcardId.some(id => id.toString() === cardsGroupData.attributes.customcard_id.toString())) return false;
    }
    if (filters.isDelayed) {
        if (!cardsGroupInRevisionStatus.includes(cardsGroupData.attributes.status)) return false;
        if (!cardsGroupData.attributes.finish_date) return false;
        if (!dateIsOlderThanToday(cardsGroupData.attributes.finish_date)) return false;
    }
    if (feature) {
        if (filters.volume && filters.volume[0] > feature.properties.real_volume) return false;
        if (filters.volume && filters.volume[1] < feature.properties.real_volume) return false;
        if (filters.area && filters.area[0] > feature.properties.real_area) return false;
        if (filters.area && filters.area[1] < feature.properties.real_area) return false;
        if (filters.surface && filters.surface[0] > feature.properties.real_surface) return false;
        if (filters.surface && filters.surface[1] < feature.properties.real_surface) return false;
        if (filters.height && filters.height[0] > feature.properties.real_height) return false;
        if (filters.height && filters.height[1] < feature.properties.real_height) return false;
        if (filters.bimElements.length) {
            if (filters.bimElements.indexOf(feature.properties.bim_element) === -1) return false;
        }
    }
    return true;
}

export function getFeaturesCardsGroupIds(map, features) {
    let cardsGroupIdsStr = features.reduce(concatCardsGroupIds, '');
    let cardsGroupIds = cardsGroupIdsStr.split(',').filter(id_ => id_ !== '');
    cardsGroupIds = removeDuplicates(cardsGroupIds);
    let passedCardsGroupIds = [];
    for (const cardsGroupId of cardsGroupIds) {
        let cardsGroupData = map.cardsGroupsData[cardsGroupId];
        if (cardsGroupFiltersPass(map.filters, cardsGroupData)) {
            passedCardsGroupIds.push(cardsGroupData.id);
        }
    }
    return passedCardsGroupIds;
}

function concatCardsGroupIds(concatenatedString, currentFeature) {
    return concatenatedString + currentFeature.properties.cards_group_ids_str;
}

export function getRelatedFeatureIds(map, feature) {
    let figuregroupIds = feature.properties.figuregroup_ids.split(',').filter(id_ => id_ !== '');
    let figuregroupIdsWithPolygons = figuregroupIds.filter(id_ => map.figuregroups_map[id_].length > 1);
    let levelId = feature.properties.level_id;
    let groupedCardsGroupIds = JSON.parse(feature.properties.grouped_cards_group_ids);
    let passedFiguregroupIds = [];
    let cardCategoryId = null;
    for (const figuregroupId of figuregroupIdsWithPolygons) {
        const cardsGroupIds = groupedCardsGroupIds[figuregroupId];
        for (const cardsGroupId of cardsGroupIds) {
            let cardsGroupData = map.cardsGroupsData[cardsGroupId];
            if (cardsGroupFiltersPass(map.filters, cardsGroupData, feature)) {
                cardCategoryId = cardsGroupData.attributes.card_category_id;
                passedFiguregroupIds.push(figuregroupId);
                break;
            }
        }   
    }
    let relatedFeatureIds = formatRelatedFeatureIds(map, feature, levelId, passedFiguregroupIds, cardCategoryId);
    return relatedFeatureIds;
}

function formatRelatedFeatureIds(map, feature, levelId, figuregroupIds, cardCategoryId) {
    let relatedFeatureIds = new Set();
    for (const figuregroupId of figuregroupIds) {
        let figureIds = map.figuregroups_map[figuregroupId];
        for (const figureId of figureIds) {
            relatedFeatureIds.add(
                parseInt(`${levelId}${figureId}${cardCategoryId}`)
            );
            relatedFeatureIds.add(
                parseInt(`${levelId}${figureId}${feature.properties.card_category_id}`)
            );
        }
    }
    return [...relatedFeatureIds];
}

export function checkTunnelConditions(cardsGroupIds, projectData) {
    if (cardsGroupIds.length > 1) return false;
    const figuregroupId = projectData.cards_groups[cardsGroupIds[0]].attributes.figuregroup_id;
    const figureIds = projectData.figuregroups_map[figuregroupId];
    // const hasOneFigure = figureIds.length === 1;
    // if (!hasOneFigure) return false;
    const gpsPoints = Object.values(projectData.figures[figureIds[0]].gps_points);
    const couldBeASquare = gpsPoints.length === 4 || gpsPoints.length === 5;
    if (couldBeASquare) {
        const lastIndex = gpsPoints.length - 1;
        const firstPoint = [gpsPoints[0].attributes.longitude, gpsPoints[0].attributes.latitude];
        const lastPoint = [gpsPoints[lastIndex].attributes.longitude, gpsPoints[lastIndex].attributes.latitude];
        const areEqual = pointsAreEqual(firstPoint, lastPoint);
        if (gpsPoints.length === 4) return !areEqual;
        if (gpsPoints.length === 5) return areEqual;
    }
    return false;
}

export function checkFailuresSelected(cardsGroupIds, projectData) {
    const failureCustomcardIds = [];
    for (const customcardData of Object.values(projectData.customcards)) {
        const protocolIsNotDefined = customcardData.attributes.protocol_id === '';
        const failureIsChecked = customcardData.attributes.add_failure;
        if (protocolIsNotDefined && failureIsChecked) {
            failureCustomcardIds.push(customcardData.id);
        }
    }
    const failureCardsGroupIds = [];
    for (const cardsGroupId of cardsGroupIds) {
        const cardsGroupData = projectData.cards_groups[cardsGroupId];
        const customcardId = cardsGroupData.attributes.customcard_id;
        if (failureCustomcardIds.includes(customcardId)) {
            failureCardsGroupIds.push(cardsGroupId);
        }
    }
    return failureCardsGroupIds;
}